import React from "react";

import type { AppCommonContext } from "@app/context/app-context";
import { useAppContext } from "@app/helpers/hooks";
import type { CurrencyCode } from "@app/types/currency";

import { Money, MoneyCeiled } from "../lib/money";

interface Props {
  price_in_cents: number;
  currency?: CurrencyCode;
  ceiled?: boolean;
  without_cents?: boolean;
  per_unit?: boolean;
  nb_units?: number;
  compact?: boolean;
  currency_display?: "symbol" | "code" | "name";
}

type GetLocalizedPrice = (
  props: Props,
  context: Pick<AppCommonContext, "locale" | "intlProvider">
) => string;

export const getLocalizedPrice: GetLocalizedPrice = (
  {
    ceiled,
    compact,
    currency,
    currency_display,
    nb_units,
    per_unit,
    price_in_cents,
    without_cents
  },
  context
) => {
  const money = ceiled
    ? new MoneyCeiled(price_in_cents, { nb_units })
    : new Money(price_in_cents, { nb_units });

  const final_currency = currency || context.intlProvider.currency;

  if (per_unit) {
    if (compact) {
      return money.getCompactPricePerUnit(
        final_currency,
        context.locale,
        currency_display
      );
    }

    if (without_cents) {
      return money.getPricePerUnitWithoutCents(
        final_currency,
        context.locale,
        currency_display
      );
    }

    return money.getPricePerUnit(
      final_currency,
      context.locale,
      currency_display
    );
  }

  if (compact) {
    return money.getCompactPrice(
      final_currency,
      context.locale,
      currency_display
    );
  }

  if (without_cents) {
    return money.getPriceWithoutCents(
      final_currency,
      context.locale,
      currency_display
    );
  }

  return money.getPrice(final_currency, context.locale, currency_display);
};

export const LocalizedPrice: React.FC<Props> = props => {
  const context = useAppContext();
  const price = getLocalizedPrice(props, context);
  return <>{price}</>;
};

const price_pattern = /(\d+\.?\d*)/;

export const getLocalizedPriceWithoutSymbol = (
  price: string
): string | undefined => price_pattern.exec(price)?.[0];
